import { useState, useMemo } from 'react';
// Models
import { IMessage } from 'app/models/ChatAI';
// React
import { Theme, Box, Typography } from '@mui/material';
// Utilities
import { parseDateFromUtcToLocal } from '_utilities/datetime.utility';
import useRefineMessageUtils from './useRefineMessageUtils';
// 
import RefineMessageText from './RefineMessageText';
import RefineMessageRelevantText from './RefineMessageRelevantText';
import RefineMessageInjestedDataPopover from './RefineMessageInjestedDataPopover';
import RefineMessageCopyButton from './RefineMessageCopyButton';
import RefineMessageNavigation from './RefineMessageNavigation';
import RefineMessageRefineButton from './RefineMessageRefineButton';

interface IProps {
  messages: IMessage[];
}

export default function RefineMessage({ messages }: IProps) {
  const [ activeIndex, setActiveIndex ] = useState(messages.length - 1);

  // Display only one active message
  const activeMessage = useMemo(() => messages[activeIndex], [messages, activeIndex]);
  const lastMessage = useMemo(() => messages[messages.length - 1], [messages]);

  const { checkIsRefineActionAllowed, analyzedPagesPercent } = useRefineMessageUtils(activeMessage);

  const isRefineActionAllowed = checkIsRefineActionAllowed(lastMessage);

  if ( !activeMessage ) return null;
  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={(theme:Theme) => ({
        backgroundColor: theme.palette.primary.light,
        borderRadius: 1,
        overflow: 'hidden'
      })}>
        <Box sx={{
          px: 4,
          pt: 4,
          pb: 2
        }}>
          <RefineMessageText message={activeMessage} />
          {activeMessage.createdOn ? (
            <Typography sx={{
              display: 'block',
              textAlign: 'right',
              fontSize: 11
            }} variant="caption">{parseDateFromUtcToLocal(activeMessage.createdOn)}</Typography>
          ) : null}
        </Box>
        <RefineMessageRelevantText
          message={activeMessage}
          lastMessage={lastMessage}
          isRefineActionAllowed={isRefineActionAllowed}
          analyzedPagesPercent={analyzedPagesPercent}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 2 }}>
        <RefineMessageInjestedDataPopover
          message={activeMessage}
        />
        <RefineMessageCopyButton message={activeMessage} />
        <RefineMessageNavigation
          messageId={activeMessage.id}
          activeIndex={activeIndex}
          onChangeActiveIndex={setActiveIndex}
          totalMessages={messages.length}
        />
        {isRefineActionAllowed && (
          <RefineMessageRefineButton
            message={activeMessage}
            lastMessage={lastMessage}
            analyzedPagesPercent={analyzedPagesPercent}
          />
        )}
      </Box>
    </Box>
  );
}
